'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SystemStatus {
  id: number;
  service: string;
  status: 'operational' | 'degraded' | 'maintenance';
  uptime: string;
  lastChecked: string;
  responseTime: string;
}

const systemServices: SystemStatus[] = [
  {
    id: 1,
    service: "PNR Validation API",
    status: "operational",
    uptime: "99.98%",
    lastChecked: "2 minutes ago",
    responseTime: "45ms"
  },
  {
    id: 2,
    service: "Booking Engine",
    status: "operational",
    uptime: "99.95%",
    lastChecked: "1 minute ago",
    responseTime: "120ms"
  },
  {
    id: 3,
    service: "Payment Gateway",
    status: "operational",
    uptime: "99.99%",
    lastChecked: "3 minutes ago",
    responseTime: "85ms"
  },
  {
    id: 4,
    service: "Airline Integration Hub",
    status: "operational",
    uptime: "99.97%",
    lastChecked: "1 minute ago",
    responseTime: "65ms"
  },
  {
    id: 5,
    service: "Customer Portal",
    status: "operational",
    uptime: "99.96%",
    lastChecked: "2 minutes ago",
    responseTime: "95ms"
  },
  {
    id: 6,
    service: "Email Notification System",
    status: "operational",
    uptime: "99.94%",
    lastChecked: "4 minutes ago",
    responseTime: "150ms"
  }
];

const statusConfig = {
  operational: {
    label: "Operational",
    color: "text-trust-green",
    bgColor: "bg-trust-green/10",
    icon: "CheckCircleIcon" as const,
    dotColor: "bg-trust-green"
  },
  degraded: {
    label: "Degraded Performance",
    color: "text-warning",
    bgColor: "bg-warning/10",
    icon: "ExclamationTriangleIcon" as const,
    dotColor: "bg-warning"
  },
  maintenance: {
    label: "Scheduled Maintenance",
    color: "text-sky-blue",
    bgColor: "bg-sky-blue/10",
    icon: "WrenchScrewdriverIcon" as const,
    dotColor: "bg-sky-blue"
  }
};

interface SystemStatusMonitorProps {
  className?: string;
}

export default function SystemStatusMonitor({ className = '' }: SystemStatusMonitorProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    setIsHydrated(true);
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const operationalCount = systemServices.filter(s => s.status === 'operational').length;
  const overallUptime = "99.97%";

  return (
    <section className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4">
            <Icon name="SignalIcon" size={32} className="text-primary" variant="solid" />
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Real-Time System Status
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Live monitoring of all critical systems ensuring 24/7 availability and performance.
          </p>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-trust-green/10 to-trust-green/5 p-6 border-b border-border">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-trust-green/20 rounded-full">
                  <Icon name="CheckCircleIcon" size={24} className="text-trust-green" variant="solid" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">All Systems Operational</div>
                  <div className="text-sm text-muted-foreground">
                    {operationalCount} of {systemServices.length} services running smoothly
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-trust-green">{overallUptime}</div>
                  <div className="text-xs text-muted-foreground">30-Day Uptime</div>
                </div>
                {isHydrated && (
                  <div className="text-center">
                    <div className="text-sm font-medium text-foreground">{currentTime}</div>
                    <div className="text-xs text-muted-foreground">Last Updated</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="divide-y divide-border">
            {systemServices.map((service) => {
              const config = statusConfig[service.status];
              return (
                <div key={service.id} className="p-6 hover:bg-surface/50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="relative mt-1">
                        <div className={`w-3 h-3 rounded-full ${config.dotColor}`} />
                        <div className={`absolute inset-0 w-3 h-3 rounded-full ${config.dotColor} animate-ping opacity-75`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                          {service.service}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Icon name="ClockIcon" size={14} />
                            <span>Uptime: {service.uptime}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="BoltIcon" size={14} />
                            <span>Response: {service.responseTime}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="ArrowPathIcon" size={14} />
                            <span>Checked {service.lastChecked}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg ${config.bgColor}`}>
                      <Icon name={config.icon} size={18} className={config.color} variant="solid" />
                      <span className={`text-sm font-medium ${config.color}`}>
                        {config.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card rounded-xl border border-border p-6 text-center">
            <Icon name="ServerIcon" size={32} className="text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-foreground mb-1">99.97%</div>
            <div className="text-sm text-muted-foreground">Average Uptime</div>
          </div>
          <div className="bg-card rounded-xl border border-border p-6 text-center">
            <Icon name="ClockIcon" size={32} className="text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-foreground mb-1">&lt;100ms</div>
            <div className="text-sm text-muted-foreground">Avg Response Time</div>
          </div>
          <div className="bg-card rounded-xl border border-border p-6 text-center">
            <Icon name="ShieldCheckIcon" size={32} className="text-primary mx-auto mb-3" />
            <div className="text-3xl font-bold text-foreground mb-1">24/7</div>
            <div className="text-sm text-muted-foreground">Monitoring Coverage</div>
          </div>
        </div>
      </div>
    </section>
  );
}